import { useCallback } from "react";

import { saveDraftProse, updateDraft } from "@/api/drafts-v2";
import { GenerationStatus } from "@/api/types/draft";

import { useCaseIdeOptional } from "../context/CaseIdeContext";
import { useCaseDataOptional } from "../data/CaseDataContext";

export type ReplyDocument = { title: string; body: string };
export type SavedReply = { draftId: string; title: string };

/**
 * Save a chat reply to the case's Work Products as an editable draft, filed at
 * the root. `null` outside a case: only cases have Work Products.
 */
export const useSaveReplyToWorkProducts = ():
  | ((reply: ReplyDocument) => Promise<SavedReply>)
  | null => {
  const ide = useCaseIdeOptional();
  const workspace = useCaseDataOptional()?.workspace ?? null;

  const save = useCallback(
    async ({ title, body }: ReplyDocument): Promise<SavedReply> => {
      if (!workspace) throw new Error("No case is open.");
      const created = await workspace.tree.createDraft(null, title);
      if (!created) throw new Error("Couldn't create the draft.");
      const draftId = created.draft.draft_id;
      try {
        await saveDraftProse(draftId, body, title);
      } catch (error) {
        // The body never landed, so the draft is still empty. Don't leave it
        // behind. A later status failure must not take this path: the prose
        // is already stored, and removing the node would drop that document.
        await workspace.tree.remove([created.node.id]);
        throw error;
      }
      // A save-back is fresh, approved content: previewable, never "Needs Update".
      // The document is already stored. Rejecting here leaves the dialog's save
      // unmarked, so a retry files a second copy and this one stays hidden.
      try {
        await updateDraft(draftId, { status: GenerationStatus.GENERATED });
      } catch {
        // `updateDraft` already reports the failure. Keep the written draft.
      }
      await workspace.refetch(["drafts"]);
      return { draftId, title };
    },
    [workspace]
  );

  return ide && workspace ? save : null;
};
